import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, API } from "@/App";
import axios from "axios";
import Marquee from "react-fast-marquee";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  LogOut,
  LayoutDashboard,
  Package,
  Settings,
  Menu,
  X,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import { FaBitcoin, FaEthereum } from "react-icons/fa";

const TradingPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [cryptoPrices, setCryptoPrices] = useState([]);
  const [selectedCoin, setSelectedCoin] = useState("bitcoin");
  const [chartData, setChartData] = useState([]);
  const [chartDays, setChartDays] = useState("7");
  const [loading, setLoading] = useState(true);

  const fetchPrices = async () => {
    try {
      const response = await axios.get(`${API}/crypto/prices`);
      setCryptoPrices(response.data || []);
    } catch (error) {
      console.error("Failed to fetch prices:", error);
    }
  };

  const fetchChart = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/crypto/chart/${selectedCoin}?days=${chartDays}`);
      const prices = response.data?.prices || [];
      const formatted = prices.map(([timestamp, price]) => ({
        time: new Date(timestamp).toLocaleDateString(),
        price: price,
      }));
      setChartData(formatted);
    } catch (error) {
      console.error("Failed to fetch chart:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrices();
    const interval = setInterval(fetchPrices, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchChart();
  }, [selectedCoin, chartDays]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getCryptoIcon = (symbol) => {
    if (symbol?.toLowerCase() === "btc") return <FaBitcoin className="w-5 h-5 text-amber-500" />;
    if (symbol?.toLowerCase() === "eth") return <FaEthereum className="w-5 h-5 text-blue-500" />;
    return <TrendingUp className="w-5 h-5 text-slate-400" />;
  };

  const selectedCoinData = cryptoPrices.find((c) => c.id === selectedCoin);

  const navItems = [
    { icon: <LayoutDashboard className="w-5 h-5" />, label: "Dashboard", href: "/dashboard" },
    { icon: <TrendingUp className="w-5 h-5" />, label: "Trading", href: "/trading", active: true },
    { icon: <Package className="w-5 h-5" />, label: "Packages", href: "/packages" },
  ];

  if (user?.role === "admin") {
    navItems.push({ icon: <Settings className="w-5 h-5" />, label: "Admin Panel", href: "/admin" });
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-luxury">
          <p className="text-slate-500 text-xs">{payload[0].payload.time}</p>
          <p className="text-platinum font-mono font-semibold text-lg">
            ${payload[0].value?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Price Ticker */}
      <div className="ticker-premium">
        <Marquee gradient={false} speed={40} className="py-2">
          <div className="flex gap-8 px-4">
            {cryptoPrices.slice(0, 10).map((coin) => (
              <div key={coin.id} className="flex items-center gap-2">
                {getCryptoIcon(coin.symbol)}
                <span className="font-medium text-slate-300 uppercase text-sm">{coin.symbol}</span>
                <span className="font-mono text-white text-sm">${coin.current_price?.toLocaleString()}</span>
                <span className={`font-mono text-xs ${coin.price_change_percentage_24h >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                  {coin.price_change_percentage_24h >= 0 ? "+" : ""}{coin.price_change_percentage_24h?.toFixed(2)}%
                </span>
              </div>
            ))}
          </div>
        </Marquee>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`fixed lg:relative inset-y-0 left-0 z-50 w-64 sidebar-premium transform transition-transform lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <Link to="/" className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-gold">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <span className="font-heading text-lg font-semibold text-platinum">AndInvest</span>
              </Link>
              <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-slate-400 hover:text-platinum">
                <X className="w-6 h-6" />
              </button>
            </div>

            <nav className="flex-1 p-4 space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    item.active
                      ? "nav-link-active"
                      : "text-slate-500 hover:bg-slate-100 hover:text-platinum"
                  }`}
                >
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                </Link>
              ))}
            </nav>

            <div className="p-4 border-t border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-100 to-amber-200 rounded-full flex items-center justify-center">
                  <span className="text-amber-700 font-semibold">{user?.name?.charAt(0)?.toUpperCase() || "U"}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-platinum font-medium truncate">{user?.name}</p>
                  <p className="text-slate-400 text-sm truncate">{user?.email}</p>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full border-slate-300 text-slate-600 hover:bg-slate-100 gap-2"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </Button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-h-screen">
          {/* Header */}
          <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 px-6 py-4 sticky top-0 z-40">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-slate-400 hover:text-platinum">
                  <Menu className="w-6 h-6" />
                </button>
                <div>
                  <h1 className="font-heading text-2xl font-semibold text-platinum">Trading</h1>
                  <p className="text-slate-500 text-sm">Live market data & charts</p>
                </div>
              </div>
              <Button
                variant="outline"
                className="border-slate-300 text-slate-600 hover:bg-slate-100 gap-2"
                onClick={() => { fetchPrices(); fetchChart(); }}
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>
          </header>

          {/* Trading Content */}
          <div className="p-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Chart Section */}
              <div className="lg:col-span-2 space-y-6">
                {/* Coin Selector */}
                <Card className="bg-white shadow-luxury border-slate-200">
                  <CardContent className="p-6">
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                      <div className="flex items-center gap-4">
                        <Select value={selectedCoin} onValueChange={setSelectedCoin}>
                          <SelectTrigger className="w-48 border-slate-300" data-testid="coin-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-slate-200">
                            {cryptoPrices.slice(0, 20).map((coin) => (
                              <SelectItem key={coin.id} value={coin.id}>
                                {coin.name} ({coin.symbol.toUpperCase()})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {selectedCoinData && (
                          <div>
                            <p className="text-3xl font-heading font-semibold text-platinum">
                              ${selectedCoinData.current_price?.toLocaleString()}
                            </p>
                            <p className={`text-sm font-mono flex items-center gap-1 ${
                              selectedCoinData.price_change_percentage_24h >= 0 ? "text-emerald-600" : "text-rose-600"
                            }`}>
                              {selectedCoinData.price_change_percentage_24h >= 0 ? (
                                <ArrowUpRight className="w-4 h-4" />
                              ) : (
                                <ArrowDownRight className="w-4 h-4" />
                              )}
                              {selectedCoinData.price_change_percentage_24h?.toFixed(2)}% (24h)
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {["1", "7", "30", "90"].map((days) => (
                          <Button
                            key={days}
                            size="sm"
                            variant={chartDays === days ? "default" : "outline"}
                            className={chartDays === days 
                              ? "btn-gold" 
                              : "border-slate-300 text-slate-600 hover:bg-slate-100"
                            }
                            onClick={() => setChartDays(days)}
                            data-testid={`chart-days-${days}`}
                          >
                            {days}D
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Chart */}
                    <div className="h-80">
                      {loading ? (
                        <div className="h-full flex items-center justify-center">
                          <div className="w-8 h-8 border-3 border-amber-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={chartData}>
                            <defs>
                              <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#FFC107" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#FFC107" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <XAxis
                              dataKey="time"
                              axisLine={false}
                              tickLine={false}
                              tick={{ fill: "#94A3B8", fontSize: 12 }}
                            />
                            <YAxis
                              axisLine={false}
                              tickLine={false}
                              tick={{ fill: "#94A3B8", fontSize: 12 }}
                              domain={["auto", "auto"]}
                              tickFormatter={(value) => `$${value.toLocaleString()}`}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Area
                              type="monotone"
                              dataKey="price"
                              stroke="#FFC107"
                              strokeWidth={2}
                              fill="url(#colorPrice)"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Market Stats */}
                {selectedCoinData && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="stat-card">
                      <CardContent className="p-4">
                        <p className="text-slate-500 text-sm mb-1">Market Cap</p>
                        <p className="text-platinum font-mono font-medium">${(selectedCoinData.market_cap / 1e9).toFixed(2)}B</p>
                      </CardContent>
                    </Card>
                    <Card className="stat-card">
                      <CardContent className="p-4">
                        <p className="text-slate-500 text-sm mb-1">24h Volume</p>
                        <p className="text-platinum font-mono font-medium">${(selectedCoinData.total_volume / 1e9).toFixed(2)}B</p>
                      </CardContent>
                    </Card>
                    <Card className="stat-card">
                      <CardContent className="p-4">
                        <p className="text-slate-500 text-sm mb-1">24h High</p>
                        <p className="text-emerald-600 font-mono font-medium">${selectedCoinData.high_24h?.toLocaleString()}</p>
                      </CardContent>
                    </Card>
                    <Card className="stat-card">
                      <CardContent className="p-4">
                        <p className="text-slate-500 text-sm mb-1">24h Low</p>
                        <p className="text-rose-600 font-mono font-medium">${selectedCoinData.low_24h?.toLocaleString()}</p>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>

              {/* Market List */}
              <Card className="bg-white shadow-luxury border-slate-200">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-platinum font-heading">Top Markets</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-2">
                    {cryptoPrices.slice(0, 10).map((coin) => (
                      <button
                        key={coin.id}
                        className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                          selectedCoin === coin.id
                            ? "bg-amber-50 border-2 border-amber-200"
                            : "hover:bg-slate-50 border-2 border-transparent"
                        }`}
                        onClick={() => setSelectedCoin(coin.id)}
                        data-testid={`market-${coin.id}`}
                      >
                        <div className="flex items-center gap-3">
                          {getCryptoIcon(coin.symbol)}
                          <div className="text-left">
                            <p className="text-platinum font-medium text-sm">{coin.symbol.toUpperCase()}</p>
                            <p className="text-slate-400 text-xs">{coin.name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-platinum font-mono text-sm">${coin.current_price?.toLocaleString()}</p>
                          <p className={`text-xs font-mono ${
                            coin.price_change_percentage_24h >= 0 ? "text-emerald-600" : "text-rose-600"
                          }`}>
                            {coin.price_change_percentage_24h >= 0 ? "+" : ""}
                            {coin.price_change_percentage_24h?.toFixed(2)}%
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
      </div>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

export default TradingPage;
